import { useState, type ChangeEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button, Input } from '@predoc/cow';
import { Info, Search, X } from 'lucide-react';
import { DatePickerInput } from '@/components/DatePickerInput';
import type { EfaxSearchState } from '../types';
import { isEmptyEfaxSearch } from '../utils/efax-search-utils';
import { EfaxProfileSelector } from './EfaxProfileSelector';
import { EfaxStatusSelector } from './EfaxStatusSelector';

interface EfaxSearchBarProps {
  search: EfaxSearchState;
  hasSearched: boolean;
  hasUnappliedChanges?: boolean;
  onChange: (search: EfaxSearchState) => void;
  onSearch: () => void;
  onClear: () => void;
}

const Label = ({ children }: { children: ReactNode }) => (
  <label className="text-sm leading-none font-medium">{children}</label>
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h3 className="flex items-center gap-1.5 font-bold">
    {children}
    <Search className="text-muted-foreground size-4" strokeWidth={2.5} />
  </h3>
);

const InlineField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="grid grid-cols-[140px_1fr] items-center gap-x-3">
    <Label>{label}</Label>
    {children}
  </div>
);

const SearchInput = ({
  value,
  onChange,
  placeholder,
  inputClassName,
}: {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  inputClassName?: string;
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
      <Input
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className={cn(
          'pl-8',
          !focused && value && 'border-blue-400 bg-blue-50',
          inputClassName,
        )}
      />
    </div>
  );
};

export const EfaxSearchBar = ({
  search,
  hasSearched,
  hasUnappliedChanges = false,
  onChange,
  onSearch,
  onClear,
}: EfaxSearchBarProps) => {
  const isSearchEmpty = isEmptyEfaxSearch(search);

  return (
    <div className="border-border w-full overflow-hidden rounded-lg border bg-white shadow-sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isSearchEmpty) {
            onSearch();
          }
        }}
      >
        <div className="px-6 pt-4">
          <h2 className="text-lg font-semibold">Search by any field</h2>
        </div>
        <div className="grid w-full grid-cols-3 gap-x-6">
          {/* Document */}
          <div className="space-y-2.5 py-6 pr-3 pl-6">
            <SectionTitle>Document</SectionTitle>

            <InlineField label="Received at">
              <DatePickerInput
                value={search.documentReceivedAt}
                onChange={(date) =>
                  onChange({ ...search, documentReceivedAt: date })
                }
                clearable
                disableDates="future"
              />
            </InlineField>

            <InlineField label="Status">
              <EfaxStatusSelector
                value={search.documentStatus}
                onChange={(documentStatus) =>
                  onChange({ ...search, documentStatus })
                }
              />
            </InlineField>

            <InlineField label="Document Name">
              <SearchInput
                value={search.documentName}
                onChange={(event) =>
                  onChange({ ...search, documentName: event.target.value })
                }
                placeholder="Document name"
              />
            </InlineField>

            <InlineField label="Document ID">
              <SearchInput
                value={search.documentId}
                onChange={(event) =>
                  onChange({ ...search, documentId: event.target.value })
                }
                placeholder="Document ID"
              />
            </InlineField>

            <InlineField label="Profile">
              <EfaxProfileSelector
                value={search.documentProfileId}
                onChange={(profileId) =>
                  onChange({ ...search, documentProfileId: profileId })
                }
              />
            </InlineField>
          </div>

          {/* Patient */}
          <div className="space-y-2.5 px-3 py-6">
            <SectionTitle>Patient</SectionTitle>

            <InlineField label="Patient Name">
              <SearchInput
                value={search.patientName}
                onChange={(event) =>
                  onChange({ ...search, patientName: event.target.value })
                }
                placeholder="Patient name"
              />
            </InlineField>

            <InlineField label="DOB">
              <DatePickerInput
                value={search.patientDob}
                onChange={(date) => onChange({ ...search, patientDob: date })}
                clearable
                disableDates="future"
              />
            </InlineField>

            <InlineField label="Patient Key">
              <SearchInput
                value={search.patientKey}
                onChange={(event) =>
                  onChange({ ...search, patientKey: event.target.value })
                }
                placeholder="Patient key"
              />
            </InlineField>

            <InlineField label="Patient Reference ID">
              <SearchInput
                value={search.patientReferenceId}
                onChange={(event) =>
                  onChange({
                    ...search,
                    patientReferenceId: event.target.value,
                  })
                }
                placeholder="Reference ID"
              />
            </InlineField>
          </div>

          {/* Providers */}
          <div className="space-y-2.5 py-6 pr-6 pl-3">
            <SectionTitle>Sending Provider</SectionTitle>

            <InlineField label="Provider Name">
              <SearchInput
                value={search.providerName}
                onChange={(event) =>
                  onChange({ ...search, providerName: event.target.value })
                }
                placeholder="Provider name"
              />
            </InlineField>

            <InlineField label="Facility Name">
              <SearchInput
                value={search.providerFacilityName}
                onChange={(event) =>
                  onChange({
                    ...search,
                    providerFacilityName: event.target.value,
                  })
                }
                placeholder="Facility name"
              />
            </InlineField>

            <InlineField label="Provider/Facility Type">
              <SearchInput
                value={search.providerType}
                onChange={(event) =>
                  onChange({ ...search, providerType: event.target.value })
                }
                placeholder="Type"
              />
            </InlineField>

            <div className="pt-4">
              <SectionTitle>Receiving Provider</SectionTitle>
            </div>

            <InlineField label="Provider Name">
              <SearchInput
                value={search.receivingProviderName}
                onChange={(event) =>
                  onChange({
                    ...search,
                    receivingProviderName: event.target.value,
                  })
                }
                placeholder="Provider name"
              />
            </InlineField>

            <InlineField label="Facility Name">
              <SearchInput
                value={search.receivingProviderFacilityName}
                onChange={(event) =>
                  onChange({
                    ...search,
                    receivingProviderFacilityName: event.target.value,
                  })
                }
                placeholder="Facility name"
              />
            </InlineField>

            <InlineField label="Provider/Facility Type">
              <SearchInput
                value={search.receivingProviderType}
                onChange={(event) =>
                  onChange({
                    ...search,
                    receivingProviderType: event.target.value,
                  })
                }
                placeholder="Type"
              />
            </InlineField>
          </div>
        </div>

        {/* Footer row */}
        <div className="border-b">
          <div className="flex w-full flex-wrap items-center justify-end gap-3 bg-zinc-50 px-4 py-2">
            <div className="flex items-center gap-2">
              {hasUnappliedChanges && (
                <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <Info className="size-3.5 shrink-0" />
                  Click Search to apply changes
                </span>
              )}
              {hasSearched ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClear}
                  aria-label="Clear search"
                  className="h-9"
                >
                  <X className="size-4" />
                  Clear
                </Button>
              ) : null}
              <Button type="submit" disabled={isSearchEmpty}>
                <Search className="size-4" />
                Search
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
